import React, { memo } from 'react';

/**
 * SectionHeader - A consistent page/section header with emoji, title, and subtitle
 * 
 * @example
 * <SectionHeader 
 *   emoji="🌍" 
 *   title="העולם סביבי" 
 *   subtitle="בואו נתרגל מילים מהעולם שלנו"
 *   titleColor="blue"
 * />
 */

export type TitleColor = 'blue' | 'purple' | 'green' | 'orange' | 'pink';

export interface SectionHeaderProps {
  /** Large emoji displayed above the title */
  emoji?: string;
  /** Main title text */
  title: string;
  /** Subtitle/description text */
  subtitle?: string;
  /** Color theme for the title */
  titleColor?: TitleColor;
  /** Whether to use gradient text for title */
  gradient?: boolean;
  /** Gradient colors (from-via-to) for gradient mode */
  gradientColors?: string;
  /** Additional CSS classes for the container */
  className?: string;
}

const titleColorStyles: Record<TitleColor, string> = {
  blue: 'text-blue-700 dark:text-blue-400',
  purple: 'text-purple-700 dark:text-purple-400',
  green: 'text-green-700 dark:text-green-400',
  orange: 'text-orange-700 dark:text-orange-400',
  pink: 'text-pink-700 dark:text-pink-400',
};

export const SectionHeader: React.FC<SectionHeaderProps> = memo(({
  emoji,
  title,
  subtitle,
  titleColor = 'blue',
  gradient = false,
  gradientColors = 'from-green-600 via-lime-500 to-emerald-500',
  className = '',
}) => {
  const titleClasses = gradient
    ? `text-transparent bg-clip-text bg-gradient-to-r ${gradientColors}`
    : titleColorStyles[titleColor];

  return (
    <div className={`text-center ${className}`}>
      {emoji && (
        <div className="text-4xl sm:text-5xl mb-2">
          {emoji}
        </div>
      )}
      <h1 className={`text-hero mb-2 ${titleClasses}`}>
        {title}
      </h1>
      {subtitle && (
        <p className="text-subtitle">
          {subtitle}
        </p>
      )}
    </div>
  );
});

SectionHeader.displayName = 'SectionHeader';
